/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The rows comparer's one select: a trigger that opens a listbox of options in
 * a portal, anchored under it. Moved out of ComparisonEngine.tsx on
 * 2026-10-03, when the column-header line (ResultHeaderRow) started to carry
 * the filters too; since 2026-10-04 the laadpalen hub's filters use its pill
 * too (src/pages/ChargerBrandsPage.tsx). Three looks on ONE listbox, one
 * portal; the listbox itself — roles, open state, every key — is useListbox
 * (src/hooks/useListbox.ts), shared with CustomDropdown since 2026-10-14:
 *
 * - `pill` (the default): the rounded pill — icon, selected label, chevron —
 *   of the laadpalen hub, and with a `label` the results bar's filter pill
 *   from sm to xl: "Looptijd: 2 jaar", the name muted, the value in the
 *   pill's own colour (2026-10-08, the filter-bar mockup's composition A).
 * - `column`: a control of the header line from xl. Since 2026-10-08 (the
 *   mockup's composition A, Jasper: "better alignment and clearer
 *   intentionality of the sort & filter option") ONE grammar for every
 *   control on that line, where the column words and the two pills were two
 *   (and the plain column words looked exactly like the filter words): 12 px
 *   semibold, 28 px tall, the word — or `<label> <value>` — and a 12 px
 *   chevron, a muted outline on hover and while open; set, the text turns
 *   emerald with a 6 px dot before it and a faint tint. The text keeps its
 *   column's edge (`edge`): the 8 px padding — 16 px with the dot — is taken
 *   back by a negative margin. A column word is what the visitor sees, so its
 *   accessible name is `<word>: <selected label>` (label-in-name, WCAG 2.5.3)
 *   and its title the selected label.
 * - `boxed`: the results bar's sort control from sm to xl, a bordered
 *   rectangle with its label ("Sorteren:") — a separate control, never one
 *   more filter pill.
 *
 * `name` names the trigger `<name>: <selected label>` and the listbox
 * `<name>` on every look. `listName` names the listbox alone, where the
 * shown label already says what the pill filters ("Land: alle" — the
 * laadpalen hub) and a `name` would double it on the trigger; until
 * 2026-10-14 the hub's four listboxes had no name at all. A value away from the default reads as set unless
 * `markSet` is false — the audience: the visitor's segment, which "wissen"
 * leaves alone and the filter count leaves out.
 *
 * A menu can carry a footer under a hairline, outside the listbox (a listbox
 * holds options only): the Voorwaarden explanation, the button to the
 * smart-charger explanation. The arrows rove the options only; Tab goes from
 * an option to the footer's control and Shift+Tab back, any other Tab closes
 * the menu and moves on from the trigger (useListbox). The menu is at most
 * 18rem wide, so a footer note wraps instead of stretching it, and it is kept
 * inside the viewport.
 *
 * SSR-safe: the menu is portaled after mount (ClientPortal), closed in the
 * bake; the trigger is static markup.
 */

import React, { useEffect, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { m, AnimatePresence } from 'motion/react';
import { ClientPortal } from '../ClientPortal';
import { cn } from '../../lib/cn';
import { useListbox } from '../../hooks/useListbox';

export interface FilterOption {
  label: string;
  value: string;
  /** What a trigger that asks for it (`short`) shows instead of `label`: the
   *  bar's sort control ("Opbrengst ↓" for "Hoogste opbrengst"), which shares
   *  its row with four filter pills. The listbox always shows the full label;
   *  so does the accessible name. */
  shortLabel?: string;
}

export interface FilterSelectProps {
  value: string;
  options: FilterOption[];
  onChange: (val: string) => void;
  defaultValue: string;
  /** An icon before the text. The header line's controls have none. */
  icon?: React.ElementType;
  /** `pill` (default), `column` (a control of the header line) or `boxed` (the bar's sort). */
  variant?: 'pill' | 'column' | 'boxed';
  /**
   * What the control is for ("Doelgroep", "Looptijd"). Set, it names the
   * trigger `<name>: <selected label>` and the listbox `<name>`; a column
   * control without a `label` shows it as its word. Unset (the laadpalen
   * hub), the name is the text shown.
   */
  name?: string;
  /**
   * The listbox's name when the control has no `name`: the laadpalen hub,
   * whose trigger keeps its shown text as its name ("Land: alle").
   */
  listName?: string;
  /**
   * A visible label before the value — "Doelgroep:", "Looptijd:",
   * "Sorteren:" (`table.filter_pill_label`) —, muted, with the value after it
   * in the control's own colour.
   */
  label?: string;
  /** What the value shows instead of the selected label ("alle" on the option that filters nothing). */
  valueText?: string;
  /** Show the selected option's `shortLabel`, where it has one, as the value. */
  short?: boolean;
  /**
   * Whether a value away from `defaultValue` reads as set (emerald). Default
   * true; false for the audience, the visitor's segment.
   */
  markSet?: boolean;
  /**
   * `column` only — the edge its text keeps: `start` (default) its column's
   * left edge, `lead` the rows' checkbox edge (the audience, which leads the
   * line), `none` where the control stands (the sort, right-aligned at the
   * line's end).
   */
  edge?: 'start' | 'lead' | 'none';
  /** The id of a description said after the name: a column's own explanation. */
  describedBy?: string;
  /**
   * Rendered under the options, below a hairline. `close` shuts the menu and
   * puts the focus back on the trigger — call it before opening anything else.
   */
  footer?: (close: () => void) => React.ReactNode;
  /** On the root — a breakpoint visibility class, for instance. */
  className?: string;
  /**
   * Several options at once (the looptijd, 2026-10-16): `value` is the host's
   * encoded selection, `isSelected` says which options it holds, a pick is a
   * toggle the host resolves in `onChange`, and the list stays open. Each
   * option shows a check box; the closed control says `valueText`.
   */
  multiple?: boolean;
  isSelected?: (optionValue: string) => boolean;
}

/** The menu never comes closer to the viewport's edge than this. */
const VIEWPORT_MARGIN = 8;

export const FilterSelect: React.FC<FilterSelectProps> = ({
  value,
  options,
  onChange,
  defaultValue,
  icon: Icon,
  variant = 'pill',
  name,
  listName,
  label,
  valueText,
  short = false,
  markSet = true,
  edge = 'start',
  describedBy,
  footer,
  className,
  multiple = false,
  isSelected,
}) => {
  const listbox = useListbox<HTMLDivElement>({ value, onChange, multiple, isSelected });
  const { open, close, triggerRef, menuRef } = listbox;
  const [pos, setPos] = useState({ top: 0, left: 0, minWidth: 0 });

  const isActive = markSet && value !== defaultValue;
  const selected = options.find((o) => o.value === value);
  // A multi-select's value may be a list no option names: its words are then
  // the host's `valueText`. 'Alle' and a single looptijd keep their full label.
  const selectedLabel = selected?.label ?? (multiple ? valueText : undefined) ?? value;
  const isColumn = variant === 'column';
  // What the closed trigger says as its value; the listbox and the name keep the full label.
  const shownValue =
    valueText ?? (short && selected?.shortLabel ? selected.shortLabel : selectedLabel);

  useEffect(() => {
    if (!open) return;
    const update = (followUp: boolean) => {
      const btn = triggerRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      // A trigger that lost its box while the menu was open — the bar hides
      // from xl, the header line below it — would pin the menu to the page's
      // top-left corner: close it instead. Only on a follow-up: the opening
      // comes from a trigger the visitor just pressed, and jsdom reports every
      // box as zeros.
      if (followUp && r.width === 0 && r.height === 0) {
        close();
        return;
      }
      const minWidth = Math.max(r.width, 148);
      // Inside the viewport: a menu as wide as its 18rem cap, opened from a
      // word near the line's right end, would otherwise run past the edge.
      // The menu is in the portal by now (ClientPortal mounted with the
      // trigger); a layout that reports no width (jsdom) is left alone.
      const viewport = document.documentElement.clientWidth;
      const width = Math.max(menuRef.current?.offsetWidth ?? 0, minWidth);
      const left =
        viewport > 0 && (menuRef.current?.offsetWidth ?? 0) > 0
          ? Math.max(VIEWPORT_MARGIN, Math.min(r.left, viewport - width - VIEWPORT_MARGIN))
          : r.left;
      setPos({ top: r.bottom + window.scrollY + 4, left: left + window.scrollX, minWidth });
    };
    update(false);
    const follow = () => update(true);
    // Capture phase catches scrolls in any ancestor scroll container so the
    // dropdown stays anchored to its trigger; passive avoids the browser
    // performance warning since `update` never calls preventDefault.
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    window.addEventListener('scroll', follow, opts);
    window.addEventListener('resize', follow);
    return () => {
      window.removeEventListener('scroll', follow, opts);
      window.removeEventListener('resize', follow);
    };
  }, [open, close, triggerRef, menuRef]);

  return (
    <div className={cn('relative shrink-0', className)}>
      <button
        type="button"
        {...listbox.triggerProps}
        aria-label={name ? `${name}: ${selectedLabel}` : undefined}
        aria-describedby={describedBy}
        // A column word does not show its value: the title says it.
        title={isColumn && !label ? selectedLabel : undefined}
        className={
          isColumn
            ? cn(
                // The type is set here, not inherited: the header line itself
                // is set in 10 px capitals for its plain column words.
                // 28 px tall on the 44 px line, a 24 px target and more
                // (WCAG 2.5.8). The hover and open outline is an inset ring,
                // so the focus ring (`focus-ring`, an outer one) stays itself.
                'relative inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-lg pr-2 text-xs font-semibold normal-case tracking-normal select-none focus-ring transition-colors duration-150',
                // 8 px before the text, 16 with the dot; on its edge the
                // padding is taken back by the margin, so the text stays put
                // when the dot comes.
                isActive ? 'pl-4' : 'pl-2',
                edge === 'start' && (isActive ? '-ml-4' : '-ml-2'),
                edge === 'lead' && (isActive ? '-ml-3.5' : '-ml-1.5'),
                // Set: emerald with a 6 px dot and a faint tint — the colour of
                // every applied filter. Light: the literal emerald-800 hex — the
                // band is 85 % opaque, and the emerald-300 token (#047857 there)
                // drops toward 4.5:1 with a dark row under the glass; #065f46
                // keeps ≥ 6:1.
                isActive
                  ? 'bg-emerald-500/12 text-emerald-300 [html.light_&]:text-[#065f46] before:absolute before:left-[5px] before:top-1/2 before:-mt-[3px] before:size-1.5 before:rounded-full before:bg-emerald-400 hover:inset-ring hover:inset-ring-emerald-500/45 aria-expanded:inset-ring aria-expanded:inset-ring-emerald-500/45'
                  : 'text-slate-300 hover:bg-slate-800/70 hover:text-fg hover:inset-ring hover:inset-ring-slate-500/55 aria-expanded:bg-slate-800/70 aria-expanded:text-fg aria-expanded:inset-ring aria-expanded:inset-ring-slate-500/55'
              )
            : variant === 'boxed'
              ? cn(
                  // A rectangle with a border, where the filters are borderless
                  // round pills. Light: a white box with a slate-400 border —
                  // the slate tokens turn the dark look into a pale field on
                  // the pale bar (and --color-white is ink there).
                  'inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[11px] font-bold select-none focus-ring transition-colors duration-150',
                  'bg-slate-800 border-slate-600/85 hover:bg-slate-700/70 [html.light_&]:bg-[#ffffff] [html.light_&]:border-[#94a3b8] [html.light_&]:hover:bg-[#f1f5f9]',
                  isActive ? 'text-emerald-300 [html.light_&]:text-[#065f46]' : 'text-slate-200'
                )
              : label
                ? cn(
                    // The bar's filter pill: a chip of its own, so the four read
                    // as four controls, 28 px tall.
                    'inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[11px] font-semibold select-none focus-ring transition-colors duration-150',
                    // Light: the literal emerald-800 hex on the tint — the
                    // emerald-300 token is #047857 there and measured 4.3:1 on
                    // the tint —, and a white chip with a slate-300 ring (the
                    // slate-800 token is the bar's own grey there).
                    isActive
                      ? 'bg-emerald-500/15 text-emerald-300 [html.light_&]:text-[#065f46] inset-ring inset-ring-emerald-500/35'
                      : 'bg-slate-800/70 text-slate-200 inset-ring inset-ring-slate-700/70 hover:bg-slate-700/60 [html.light_&]:bg-[#ffffff] [html.light_&]:inset-ring-[#cbd5e1] [html.light_&]:hover:bg-[#f1f5f9]'
                  )
                : cn(
                    'inline-flex items-center gap-1.5 py-1.5 px-2.5 rounded-full text-[11px] font-semibold transition-all duration-200 whitespace-nowrap select-none',
                    // Light: the literal emerald-800 hex on the tinted pill — the
                    // emerald-300 token is already #047857 there and measured 4.3:1 on
                    // the tint, and the emerald-800 token is remapped to a pastel.
                    isActive
                      ? 'bg-emerald-500/15 text-emerald-300 [html.light_&]:text-[#065f46] ring-1 ring-emerald-500/30 shadow-sm shadow-emerald-900/10'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
                  )
        }
      >
        {Icon && <Icon className="w-3 h-3 opacity-70 shrink-0" />}
        {label ? (
          <>
            {/* The name, muted; the value in the control's own colour. */}
            <span className="font-medium text-fg-muted">{label}</span>
            <span>{shownValue}</span>
          </>
        ) : isColumn ? (
          (name ?? shownValue)
        ) : (
          shownValue
        )}
        <ChevronDown
          className={cn(
            isColumn ? 'w-3 h-3 shrink-0 text-fg-muted' : 'w-2.5 h-2.5',
            'transition-transform duration-200 motion-reduce:transition-none',
            !isColumn && 'opacity-50',
            open && 'rotate-180'
          )}
        />
      </button>

      <ClientPortal>
        <AnimatePresence>
          {open && (
            <m.div
              {...listbox.menuProps}
              onAnimationComplete={listbox.onMenuShown}
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.13 }}
              style={{
                position: 'absolute',
                top: pos.top,
                left: pos.left,
                minWidth: pos.minWidth,
              }}
              className="z-[9999] max-w-72 bg-slate-800 border border-slate-700/60 rounded-xl shadow-2xl shadow-slate-950/50 overflow-hidden"
            >
              {/* A long list (the laadpalen hub's twenty countries) scrolls
                  inside the menu; the footer stays in view under it. */}
              <div
                {...listbox.listboxProps}
                tabIndex={-1}
                aria-label={name ?? listName}
                className="max-h-[min(22rem,60vh)] overflow-y-auto overscroll-contain"
              >
                {options.map((opt) => {
                  const chosen =
                    multiple && isSelected ? isSelected(opt.value) : opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      {...listbox.optionProps(opt.value)}
                      className={cn(
                        'w-full text-left px-4 py-2.5 text-xs font-medium transition-colors',
                        multiple && 'flex items-center gap-2.5',
                        chosen
                          ? 'text-emerald-400 bg-emerald-900/20 font-semibold'
                          : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                      )}
                    >
                      {multiple && (
                        // A check box, so "chosen" is a shape as well as a colour (WCAG 1.4.1).
                        <span
                          aria-hidden="true"
                          className={cn(
                            'inline-flex size-4 shrink-0 items-center justify-center rounded border',
                            chosen
                              ? 'border-emerald-500 bg-emerald-500 text-slate-950'
                              : 'border-slate-500'
                          )}
                        >
                          {chosen && <Check className="size-3" strokeWidth={3} />}
                        </span>
                      )}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {footer && (
                <div data-menu-footer className="border-t border-slate-700/60">
                  {footer(listbox.closeAndFocusTrigger)}
                </div>
              )}
            </m.div>
          )}
        </AnimatePresence>
      </ClientPortal>
    </div>
  );
};
